import axios from 'axios';
import * as Sentry from '@sentry/react';

// Create the parent span
const parentSpan = Sentry.getActiveSpan();

const RETRY_CONFIG = {
  retries: 3,
  retryDelay: (attempt) => Math.min(1000 * Math.pow(2, attempt), 10000), // Exponential backoff
  retryCondition: (error) => {
    // Retry on network errors, 5xx errors, and specific 4xx errors
    return (
      !error.response ||
      error.response.status >= 500 ||
      error.code === 'NETWORK_ERROR' ||
      error.code === 'ECONNABORTED' ||
      error.message.includes('Network Error') ||
      error.message.includes('timeout')
    );
  }
};

const axiosInstance = axios.create({
  // withCredentials: false,
  timeout: 10000, // Add timeout to prevent hanging requests
});

//  Enhanced retry logic
const retryRequest = async (config, attempt = 0) => {
  try {
    return await axiosInstance(config);
  } catch (error) {
    const shouldRetry = 
      attempt < RETRY_CONFIG.retries && 
      RETRY_CONFIG.retryCondition(error);

    if (shouldRetry) {
      const delay = RETRY_CONFIG.retryDelay(attempt);
      console.warn(`Retrying request (attempt ${attempt + 1}/${RETRY_CONFIG.retries}) after ${delay}ms:`, error.message);
      
      // Track retry attempts
      if (window.Sentry) {
        Sentry.addBreadcrumb({
          message: `Network request retry attempt ${attempt + 1}`,
          category: 'network',
          level: 'warning',
          data: {
            url: config.url,
            method: config.method,
            error: error.message,
            attempt: attempt + 1
          }
        });
      }

      await new Promise(resolve => setTimeout(resolve, delay));
      return retryRequest(config, attempt + 1);
    }
    
    throw error;
  }
};

// Helper to get file size in MB
const getFileSizeInMB = (file) => {
  if (file instanceof File || file instanceof Blob) {
    return (file.size / (1024 * 1024)).toFixed(2);
  }
  return null;
};

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const startTime = performance.now();
    const uploadStartTime = new Date().getTime();
    const isPhotoUpload = config.data instanceof FormData && config.data.has('image');
    
    // Create a simple span object for tracking without Sentry.startSpan
    const span = {
      data: {},
      setData: function(newData) {
        Object.assign(this.data, newData);
      },
      setStatus: function(status) {
        this.status = status;
      },
      end: function() {
        // Log span completion for debugging
        console.debug('Span ended:', this.data);
      }
    };

    // Set initial span data
    span.setData({
      url: config.url,
      method: config.method?.toUpperCase(),
      requestTime: new Date().toISOString(),
      isPhotoUpload: isPhotoUpload,
      request_size: JSON.stringify(config.data || {}).length,
      has_timeout: !!config.timeout,
      timeout_ms: config.timeout || 0,
    });

    if(isPhotoUpload) {
      span.setData({
        photoCode: config.data.get('photo_code') || null,
        imageSize: getFileSizeInMB(config.data.get('image')) + 'MB' || null,
      });
    }

    // Store metadata for response interceptor
    config.metadata = {
      span,
      startTime,
      isPhotoUpload,
      uploadStartTime: isPhotoUpload ? uploadStartTime : null,
    };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    const { span, startTime, isPhotoUpload, uploadStartTime } =
      response.config.metadata || {};
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (span) {
      // Add base performance data
      span.setData({
        responseStatus: response.status,
        responseTime: new Date().toISOString(),
        duration: `${duration.toFixed(2)}ms`,
        responseSize: JSON.stringify(response.data).length,
        headersSize: JSON.stringify(response.headers).length,
        cacheHit: response.headers['x-cache'] === 'HIT',
        duration_ms: Math.round(duration),
      });

      // Add photo upload specific data
      if (isPhotoUpload) {
        const uploadEndTime = new Date().getTime();
        const uploadDuration = uploadEndTime - uploadStartTime;
        const uploadSpeed = response.config.data.get('image')?.size
          ? `${(
              ((response.config.data.get('image').size / uploadDuration) *
                1000) /
              (1024 * 1024)
            ).toFixed(2)}MB/s`
          : null;

        span.setData({
          uploadDuration: `${(uploadDuration / 1000).toFixed(2)}s`,
          uploadSpeed,
          uploadSuccess: true,
          upload_duration_s: Math.round(uploadDuration / 1000),
        });
      }

      if (duration > 100) {
        span.setData({
          slow_request: true,
        });
      }

      // Set status and finish the span
      span.setStatus('ok');
      span.end();
    }

    return response;
  },
  (error) => {
    const { span, startTime, isPhotoUpload, uploadStartTime } =
      error.config?.metadata || {};
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (span) {
      // Add base error data
      span.setData({
        error: true,
        errorMessage: error.message,
        errorTime: new Date().toISOString(),
        duration: `${duration.toFixed(2)}ms`,
        errorResponse: error.response?.data,
        errorStatus: error.response?.status,
        error_type: error.name,
        duration_ms: Math.round(duration),
        error_category: classifyError(error),
        is_retryable: RETRY_CONFIG.retryCondition(error),
        network_error: error.code === 'NETWORK_ERROR' || error.message.includes('Network Error'),
      });

      // Add photo upload specific error data
      if (isPhotoUpload) {
        const uploadEndTime = new Date().getTime();
        const uploadDuration = uploadEndTime - uploadStartTime;

        span.setData({
          uploadDuration: `${(uploadDuration / 1000).toFixed(2)}s`,
          uploadSuccess: false,
          failureReason: error.message,
          upload_duration_s: Math.round(uploadDuration / 1000),
          upload_error: error.message,
        });
      }

      if (error.response) {
        span.setData({
          error_status: error.response.status,
        });
      }

      // Set error status and finish the span
      span.setStatus('error');
      span.end();
    }

    // Enhanced Sentry error reporting with context
    if (error.response?.status >= 500 || isPhotoUpload || !error.response) {
      Sentry.captureException(error, {
        tags: {
          error_type: classifyError(error),
          is_network_error: error.code === 'NETWORK_ERROR' || error.message.includes('Network Error'),
          has_response: !!error.response,
          status_code: error.response?.status || 'no_response'
        },
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          duration: duration,
          response: error.response?.data,
          isPhotoUpload: isPhotoUpload,
          photoCode: error.config?.data?.get('photo_code'),
          userAgent: navigator.userAgent,
          connectionType: navigator.connection?.effectiveType,
          onLine: navigator.onLine
        },
      });
    }

    return Promise.reject(error);
  }
);

const classifyError = (error) => {
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return 'timeout';
    }
    if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
      return 'network';
    }
    return 'connection';
  }
  
  const status = error.response.status;
  if (status >= 500) return 'server_error';
  if (status >= 400) return 'client_error';
  return 'unknown';
};

//  Override the default request method to use retry logic
const originalRequest = axiosInstance.request;
axiosInstance.request = function(config) {
  return retryRequest(config);
};

export default axiosInstance;
